/**
 * Centralized Logger Utility for Frontend
 * 
 * This utility provides a centralized way to handle logging.
 * All console.log statements should be replaced with logger.log()
 * 
 * Usage:
 * import logger from '@/utils/logger';
 * logger.log('Your message');
 * logger.error('Error message');
 * logger.warn('Warning message');
 */

// Import config - we'll need to handle this differently for frontend
// Since frontend can't directly require Node.js modules, we'll use environment variables
// Vue CLI uses process.env (not import.meta.env like Vite)
// Environment variables in Vue CLI need to be prefixed with VUE_APP_
const env = typeof process !== 'undefined' && process.env ? process.env : {};

// Get and trim the environment variable value (handle whitespace)
// Vue CLI embeds env vars at build time, so we need to check process.env directly
const enableLoggingValue = (process.env.VUE_APP_ENABLE_LOGGING || env.VUE_APP_ENABLE_LOGGING || '').toString().trim().toLowerCase();

// Determine if logging is enabled
// Priority: 1) If VUE_APP_ENABLE_LOGGING is explicitly 'false' -> disable (highest priority)
//           2) If VUE_APP_ENABLE_LOGGING is explicitly 'true' -> enable
//           3) If not set, enable only if NODE_ENV !== 'production'
const isLoggingEnabled = enableLoggingValue === 'false'
  ? false
  : (enableLoggingValue === 'true' 
      ? true 
      : process.env.NODE_ENV !== 'production');

// FORCE DISABLE if explicitly set to false (defensive check)
const FORCE_DISABLE = enableLoggingValue === 'false';

const loggerConfig = {
  ENABLE_LOGGING: isLoggingEnabled,
  LOG_LEVEL: env.VUE_APP_LOG_LEVEL || 'info',
  NODE_ENV: process.env.NODE_ENV || 'development'
};

class Logger {
  constructor() {
    this.enabled = loggerConfig.ENABLE_LOGGING;
    this.logLevel = loggerConfig.LOG_LEVEL;
    
    // ALWAYS show debug info in development (bypasses logger to show even when disabled)
    // This helps diagnose why logging might still be enabled
    if (process.env.NODE_ENV === 'development') {
      if (!window.__loggerConfigShown) {
        // Use direct console.log to bypass logger - this MUST show
        console.log('%c[Logger Config Debug]', 'color: orange; font-weight: bold; font-size: 14px;', {
          'VUE_APP_ENABLE_LOGGING (raw)': process.env.VUE_APP_ENABLE_LOGGING || env.VUE_APP_ENABLE_LOGGING || 'NOT SET',
          'enableLoggingValue (processed)': enableLoggingValue,
          'FORCE_DISABLE': FORCE_DISABLE,
          'isLoggingEnabled': isLoggingEnabled,
          'Final enabled state': this.enabled,
          'LOG_LEVEL': this.logLevel,
          'NODE_ENV': process.env.NODE_ENV,
          'All VUE_APP_ keys': Object.keys(process.env).filter(k => k.startsWith('VUE_APP_'))
        });
        window.__loggerConfigShown = true;
      }
    }
  }

  /**
   * Check if logging is enabled for the given level
   */
  shouldLog(level) {
    // Force disable if VUE_APP_ENABLE_LOGGING is explicitly 'false'
    if (FORCE_DISABLE) return false;
    if (!this.enabled) return false;
    
    const levels = { debug: 0, info: 1, warn: 2, error: 3 };
    const currentLevel = levels[this.logLevel] || 1;
    const messageLevel = levels[level] || 1;
    
    return messageLevel >= currentLevel;
  }

  /**
   * General log method (replaces console.log)
   */
  log(...args) {
    if (this.shouldLog('info')) {
      console.log(...args);
    }
  }

  /**
   * Debug log
   */
  debug(...args) {
    if (this.shouldLog('debug')) {
      console.debug(...args);
    }
  }

  /**
   * Info log
   */
  info(...args) {
    if (this.shouldLog('info')) {
      console.info(...args);
    }
  }

  /**
   * Warning log
   */
  warn(...args) {
    if (this.shouldLog('warn')) {
      console.warn(...args);
    }
  }

  /**
   * Error log (always enabled)
   */
  error(...args) {
    // Errors are always logged for debugging purposes
    console.error(...args);
  }
}

// Export singleton instance
export default new Logger();

